/**
 * Portal Context
 *
 * Manages the active portal view (User vs IT) and provides portal-aware
 * navigation and access control. IT staff can toggle between portals,
 * while regular users only see the User portal.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

// ============================================
// Types
// ============================================

export type PortalType = 'user' | 'it';

export interface PortalContextValue {
  /** The currently active portal */
  activePortal: PortalType;

  /** Whether the user can access the IT portal */
  hasITAccess: boolean;

  /** Switch to a different portal (only works if user has access) */
  setPortal: (portal: PortalType) => void;

  /** Toggle between portals (only works if user has access to both) */
  togglePortal: () => void;

  /** Check if a specific portal is active */
  isPortalActive: (portal: PortalType) => boolean;
}

interface PortalProviderProps {
  children: ReactNode;
}

// ============================================
// Constants
// ============================================

const PORTAL_STORAGE_KEY = 'percepta-active-portal';

// ============================================
// Context
// ============================================

const PortalContext = createContext<PortalContextValue | null>(null);

// ============================================
// Helper Functions
// ============================================

/**
 * Gets the default portal for a user based on IT access
 */
function getDefaultPortal(hasITAccess: boolean): PortalType {
  return hasITAccess ? 'it' : 'user';
}

/**
 * Loads the persisted portal preference from localStorage
 */
function loadPersistedPortal(hasITAccess: boolean): PortalType {
  try {
    const stored = localStorage.getItem(PORTAL_STORAGE_KEY);
    if (stored === 'user' || stored === 'it') {
      // Validate that user has access to the stored portal
      if (stored === 'it' && !hasITAccess) {
        return 'user';
      }
      return stored;
    }
  } catch {
    // localStorage not available or error reading
  }
  return getDefaultPortal(hasITAccess);
}

/**
 * Persists the portal preference to localStorage
 */
function persistPortal(portal: PortalType): void {
  try {
    localStorage.setItem(PORTAL_STORAGE_KEY, portal);
  } catch {
    // localStorage not available or quota exceeded
  }
}

// ============================================
// Provider Component
// ============================================

export function PortalProvider({ children }: PortalProviderProps) {
  const { hasITAccess } = useAuth();

  const [activePortal, setActivePortal] = useState<PortalType>(() =>
    loadPersistedPortal(hasITAccess)
  );

  // Persist portal changes
  useEffect(() => {
    persistPortal(activePortal);
  }, [activePortal]);

  // Reset to appropriate portal if user loses IT access
  useEffect(() => {
    if (activePortal === 'it' && !hasITAccess) {
      setActivePortal('user');
    }
  }, [activePortal, hasITAccess]);

  const setPortal = useCallback(
    (portal: PortalType) => {
      // Only allow IT portal if user has access
      if (portal === 'it' && !hasITAccess) {
        return;
      }
      setActivePortal(portal);
    },
    [hasITAccess]
  );

  const togglePortal = useCallback(() => {
    if (!hasITAccess) {
      return;
    }
    setActivePortal((current) => (current === 'user' ? 'it' : 'user'));
  }, [hasITAccess]);

  const isPortalActive = useCallback(
    (portal: PortalType) => activePortal === portal,
    [activePortal]
  );

  const value = useMemo<PortalContextValue>(
    () => ({
      activePortal,
      hasITAccess,
      setPortal,
      togglePortal,
      isPortalActive,
    }),
    [activePortal, hasITAccess, setPortal, togglePortal, isPortalActive]
  );

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access portal context
 * @throws Error if used outside of PortalProvider
 */
export function usePortal(): PortalContextValue {
  const context = useContext(PortalContext);

  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }

  return context;
}

/**
 * Hook to check if a specific portal is active
 * Safe to use outside of PortalProvider (returns false)
 */
export function useIsPortalActive(portal: PortalType): boolean {
  const context = useContext(PortalContext);
  return context?.activePortal === portal;
}
